import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { LinkProps, StyledIcon } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
// eslint-disable-next-line spire/fenced-imports
import CarosulArrow from "@tIntermatic/Icons/CarosulArrow";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;
const downIcon = <StyledIcon src={CarosulArrow} size={9} />;

export const Slash = styled(Typography as any).attrs({ children: downIcon })`
    margin: 0 2px 0 7px;
`;

export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;
    overflow-x: auto;
    scroll-behavior: smooth;
    max-width: 1140px;
    margin: 0 auto;
    ::-webkit-scrollbar {
        width: 3px;
        height: 3px;
    }

    ::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
    }
    ::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 10px;
    }
    ::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
    ${injectCss}
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    max-width: 100% !important;
                    padding: 0 30px;
                `,
                css`
                    max-width: 100% !important;
                    padding: 0 30px;
                `,
                css`
                    max-width: 720px !important;
                    padding: 0;
                `,
                css`
                    max-width: 960px !important;
                    padding: 0;
                `,
                css`
                    max-width: 1140px !important;
                    padding: 0;
                `,
            ],
            "min",
        )}
`;

const Breadcrumbs: React.FC<BreadcrumbsProps> = withTheme(({ links, mergeCss, ...otherProps }) => {
    const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
    const typographyProps = spreadProps("typographyProps");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;
    // return null if `links` is an empty empty object, collection, map, or set.
    if (!links || links.length === 0) {
        return null;
    }
    const children = links.map((link: any, index: number) => {
        const children = link.children;
        let classNames = "";

        if (children === "Home") {
            classNames = classNames.concat("notTruncate");
        } else {
            classNames = classNames.concat("breadcrumbLink");
        }

        // yield null if no children
        if (!React.Children.count(children)) {
            return null;
        }
        const key = typeof children === "string" ? children : index;
        if (index === links.length - 1) {
            return (
                <Typography
                    mergeCss={resolvedMergeCss}
                    {...typographyProps}
                    aria-current="page"
                    key={key}
                    css={css`
                        font-size: 14px;
                        font-weight: 500;
                        letter-spacing: 0;
                        line-height: 40px;
                        white-space: nowrap;
                        text-transform: none;
                        color: rgb(153, 153, 153);
                    `}
                >
                    {children}
                </Typography>
            );
        }
        return (
            <React.Fragment key={key}>
                <Link
                    typographyProps={typographyProps}
                    {...link}
                    className={classNames}
                    css={css`
                        span {
                            font-size: 14px;
                            letter-spacing: 0;
                            line-height: 40px;
                            color: #33333a !important;
                            white-space: nowrap;
                        }
                    `}
                />
                <Slash {...typographyProps} />
            </React.Fragment>
        );
    });
    // return null if `links` map yields an array of nulls
    if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
        return null;
    }

    return (
        <StyledNav {...otherProps} className="breadcrumbsbWrapper" css={applyStyledProp("css", resolvedMergeCss)}>
            {children}
        </StyledNav>
    );
});

/** @component */
export default Breadcrumbs;
